import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { AvatarWrapper, DataTableWrapper } from '../styles/globalStyle';
import { DataTableProps } from '../types';

const DataTable = <T extends object>({
  columns,
  data,
  actions,
}: DataTableProps<T>) => {
  const getValueByPath = (obj: any, path: string) => {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  };

  const formatCell = (value: any, col: any) => {
    if (value == null) return '-';

    if (Array.isArray(value)) {
      return value.length;
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const date = new Date(value);
      return date.toLocaleDateString();
    }

    if (typeof value === 'boolean') {
      if (col?.booleanLabels) {
        return value ? col.booleanLabels.true : col.booleanLabels.false;
      }
      return value ? 'Active' : 'Inactive';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value.toString();
  };

  const chipStyles: Record<
    string,
    (value: any) => { bg: string; color: string }
  > = {
    type: value => {
      const map: any = {
        Admin: { bg: '#dbeafe', color: '#1d4ed8' },
        Staff: { bg: '#fef3c7', color: '#d97706' },
        Exhibitor: { bg: '#dcfce7', color: '#16a34a' },
        Delegate: { bg: '#fef3c7', color: '#d97706' },
        Speaker: { bg: '#f3e8ff', color: '#7c3aed' },
        Visitor: { bg: '#e0e7ff', color: '#3730a3' },
      };
      return map[value] || { bg: '#f3f4f6', color: '#374151' };
    },

    status: value => {
      return value
        ? { bg: '#dcfce7', color: '#16a34a' }
        : { bg: '#fee2e2', color: '#dc2626' };
    },
  };

  const renderChip = (value: any, cal: any) => {
    const styleFn = chipStyles[cal.chip];
    const style = styleFn
      ? styleFn(value)
      : { bg: '#e5e7eb', color: '#374151' };

    return (
      <Chip
        label={formatCell(value, cal)}
        size="small"
        sx={{
          bgcolor: style.bg,
          color: style.color,
          fontSize: '0.75rem',
          fontWeight: 500,
          height: 24,
        }}
      />
    );
  };

  const getColumnValues = (row: any, col: any) => {
    if (col.keys && Array.isArray(col.keys)) {
      return col.keys.map((key: string) =>
        key.includes('.') ? getValueByPath(row, key) : row[key]
      );
    }

    if (col.key) {
      return [
        col.key.includes('.') ? getValueByPath(row, col.key) : row[col.key],
      ];
    }

    return [];
  };

  const autoRenderGroup = (rawValues: any[]) => {
    // Auto date range formatting if both values are dates
    if (
      rawValues.length === 2 &&
      rawValues.every((v: any) => v && !isNaN(new Date(v).getTime()))
    ) {
      const f = (d: any) =>
        new Date(d).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });

      return `${f(rawValues[0])} - ${f(rawValues[1])}`;
    }

    return rawValues.filter(Boolean).join(' ');
  };

  return (
    <>
      <DataTableWrapper>
        <Table className="table">
          <TableHead className="table_head">
            <TableRow className="table_row">
              {columns &&
                columns.map((column, i) => (
                  <TableCell className="table_data" key={i}>
                    {column.label}
                  </TableCell>
                ))}
              {actions && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody className="table_body">
            {data.map((row, rowIndex) => (
              <TableRow className="table_row" key={rowIndex}>
                {columns.map((col, colIndex) => {
                  const rawValues = getColumnValues(row, col);

                  const cellContent = col.renderGroup
                    ? col.renderGroup(rawValues, row)
                    : col.keys
                      ? autoRenderGroup(rawValues)
                      : col.chip
                        ? renderChip(rawValues[0], col)
                        : formatCell(rawValues[0], col);
                  return (
                    <TableCell className="table_data" key={colIndex}>
                      {cellContent}
                    </TableCell>
                  );
                })}
                {actions && (
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {actions(row).map((action, i) => (
                        <IconButton
                          key={i}
                          size="small"
                          onClick={e => action.onClick(e, row)}
                        >
                          {action.icon}
                        </IconButton>
                      ))}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (actions ? 1 : 0)}
                  sx={{ textAlign: 'center', py: 3 }}
                >
                  No Data Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DataTableWrapper>
    </>
  );
};

export default DataTable;
